import { AbstractControl, FormControl, ValidationErrors } from '@angular/forms';

/**
 * Automatically formats a landline number by inserting a dash after the STD code.
 * Accepts only FormControl as input.
 *
 * Example:
 *  Input: 02212345678 ➜ Output: 022-12345678
 *  Input: 04423456789 ➜ Output: 044-23456789
 */
export function formatPhoneNumber(control: FormControl): void {
  let value: string = control.value || '';

  // Clean up: remove spaces and dashes
  value = value.replace(/\s+/g, '').replace(/-/g, '');

  // Proceed only if it's a landline pattern
  if (value.startsWith('0') && value.length >= 11) {
    let stdCodeLength = 3;

    // Common 3-digit STD codes — tweak this as needed
    const known3DigitSTDCodes = ['011', '022', '033', '044', '080'];

    if (!known3DigitSTDCodes.includes(value.substring(0, 3))) {
      stdCodeLength = 4; // Assume 4-digit STD code if not found in 3-digit list
    }

    const std = value.substring(0, stdCodeLength);
    const number = value.substring(stdCodeLength);

    // Set the formatted value without triggering re-validation
    control.setValue(`${std}-${number}`, { emitEvent: false });
  }
}



/**
 * Converts a File object (e.g., from an <input type="file">) to a Base64-encoded string.
 * Returns a Promise that resolves with the Base64 string once the file is read.
 *
 * Example usage:
 *  toBase64(file).then(base64 => console.log(base64));
 *
 * @param file - The File object to be converted.
 * @returns A Promise that resolves with the Base64-encoded string.
 */
export function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
}



export function lowercaseFirstLetter(str: string): string {
  if (!str) return str;
  return str.charAt(0).toLowerCase() + str.slice(1);
}


export function lowercaseFirstLetterKeys(obj: { [key: string]: any }): { [key: string]: any } {
  return Object.keys(obj).reduce((acc, key) => {
    const newKey = key.charAt(0).toLowerCase() + key.slice(1);
    acc[newKey] = obj[key];
    return acc;
  }, {} as Record<string, any>);
}



export function formatDateForInput(date: string | Date): string {
  const d = new Date(date);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function formatTimeToInput(timeString: string): string {
  if (!timeString) return '';
  return timeString.substring(0, 5); // Extracts "HH:mm"
}

export function formatTimeForReq(time: string): string | null {
  if (!time) return null;
  const [hh, mm] = time.split(':');
  return `${hh.padStart(2, '0')}:${mm.padStart(2, '0')}:00`; // convert to hh:mm:ss
};

export function convertAmountToWords(amount: number): string {
  const ones = [
    '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
    'Seventeen', 'Eighteen', 'Nineteen'
  ];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const convert_hundreds = (n: number): string => {
    let str = '';
    if (n > 99) {
      str += ones[Math.floor(n / 100)] + ' Hundred ';
      n %= 100;
    }
    if (n > 0) {
      if (n < 20) str += ones[n];
      else str += tens[Math.floor(n / 10)] + ' ' + ones[n % 10];
    }
    return str.trim();
  };

  const convert = (n: number): string => {
    if (n === 0) return 'Zero';

    let result = '';
    const crore = Math.floor(n / 10000000);
    const lakh = Math.floor((n % 10000000) / 100000);
    const thousand = Math.floor((n % 100000) / 1000);
    const hundreds = n % 1000;

    if (crore) result += convert_hundreds(crore) + ' Crore ';
    if (lakh) result += convert_hundreds(lakh) + ' Lakh ';
    if (thousand) result += convert_hundreds(thousand) + ' Thousand ';
    if (hundreds) result += convert_hundreds(hundreds);

    return result.trim();
  };

  const rupees = Math.floor(amount);
  const paise = Math.round((amount - rupees) * 100);

  let words = convert(rupees) + ' Rupees';
  words += paise > 0 ? ` And ${convert(paise)} Paise` : ' Only';

  return words;
}


export function formatIndianCurrency(amount: number): string {
  if (!amount || amount < 0) return '';
  if (amount >= 10000000) {
    const crores = amount / 10000000;
    return crores % 1 === 0 ? `${crores}Cr` : `${crores.toFixed(1)}Cr`;
  } else if (amount >= 100000) {
    const lakhs = amount / 100000;
    return lakhs % 1 === 0 ? `${lakhs}L` : `${lakhs.toFixed(1)}L`;
  } else if (amount >= 1000) {
    const thousands = amount / 1000;
    return thousands % 1 === 0 ? `${thousands}K` : `${thousands.toFixed(1)}K`;
  } else {
    return amount.toLocaleString('en-IN');
  }
}


/**
 * Binds a FormControl to a formatted string property
 */
export function bindAmountFormatter(control: FormControl, setFormatted: (value: string) => void) {
  control.valueChanges.subscribe(value => {
    setFormatted(formatIndianCurrency(value || 0));
  });
  // Initialize immediately
  setFormatted(formatIndianCurrency(control.value || 0));
}


export function handleAadhaarInput(event: KeyboardEvent) {
  if (!/[0-9]/.test(event.key)) {
    event.preventDefault();
  }
}
// utils/input-format.util.ts
export function formatPanInput(value: string): string {
  return value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
}

export function notTodayOrFutureValidator(control: AbstractControl): ValidationErrors | null {
  const inputDate = new Date(control.value);
  const today = new Date();

  // Normalize both dates to remove the time portion
  inputDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  // If date is today or future, return error
  if (inputDate.getTime() >= today.getTime()) {
    return { notTodayOrFuture: true };
  }

  return null; // ✅ Valid (date is before today)
}

export function isMobileDevice(): boolean {
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  return /android|iphone|ipad|ipod|iemobile|blackberry|bada/i.test(userAgent.toLowerCase());
}
